import http, { HttpError } from '~/shared/http';
import { jsonRequestHeaders } from '~/csrf';
import { me } from '~/actions/App/Http/Controllers/AuthController';

interface PublicAuthUser {
  id: number;
  name: string;
  email: string;
  analytics_id?: string | null;
}

interface PublicAuthState {
  authenticated: boolean;
  features: {
    accountLikes: boolean;
    accountDashboard: boolean;
    podcastVerification: boolean;
  };
  user: PublicAuthUser | null;
}

declare global {
  interface Window {
    __PUBLIC_AUTH__?: {
      features: PublicAuthState['features'];
      user: PublicAuthUser | null;
    };
  }
}

export function getPublicAuthUser(): PublicAuthUser | null {
  return window.__PUBLIC_AUTH__?.user ?? null;
}

export function getPublicAuthFeatures(): PublicAuthState['features'] {
  return (
    window.__PUBLIC_AUTH__?.features ?? {
      accountLikes: false,
      accountDashboard: false,
      podcastVerification: false,
    }
  );
}

function setPublicAuthState(state: PublicAuthState): void {
  window.__PUBLIC_AUTH__ = {
    features: state.features,
    user: state.authenticated ? state.user : null,
  };
}

export function setPublicAuthUser(
  user: PublicAuthUser | null,
  features: Partial<PublicAuthState['features']> = {},
): void {
  publicAuthResolved = true;
  setPublicAuthState({
    authenticated: user !== null,
    features: {
      accountLikes: features.accountLikes ?? false,
      accountDashboard: features.accountDashboard ?? false,
      podcastVerification: features.podcastVerification ?? false,
    },
    user,
  });
}

export function isPubliclyAuthenticated(): boolean {
  return getPublicAuthUser() !== null;
}

let pendingPublicAuthRequest: Promise<PublicAuthState> | null = null;
let publicAuthResolved = false;

export async function loadPublicAuthState(force = false): Promise<PublicAuthState> {
  if (publicAuthResolved && !force) {
    return {
      authenticated: isPubliclyAuthenticated(),
      features: getPublicAuthFeatures(),
      user: getPublicAuthUser(),
    };
  }

  if (pendingPublicAuthRequest && !force) {
    return pendingPublicAuthRequest;
  }

  pendingPublicAuthRequest = http
    .get(me.url(), {
      headers: jsonRequestHeaders(),
    })
    .then(({ data }) => {
      const state: PublicAuthState = {
        authenticated: Boolean(data?.data?.authenticated),
        features: {
          accountLikes: Boolean(data?.data?.features?.accountLikes),
          accountDashboard: Boolean(data?.data?.features?.accountDashboard),
          podcastVerification: Boolean(data?.data?.features?.podcastVerification),
        },
        user: data?.data?.user ?? null,
      };

      publicAuthResolved = true;
      setPublicAuthState(state);

      return state;
    })
    .catch((error) => {
      console.error('Failed to load auth state:', error);

      const state: PublicAuthState = {
        authenticated: false,
        features: {
          accountLikes: false,
          accountDashboard: false,
          podcastVerification: false,
        },
        user: null,
      };

      // Only mark as resolved if server definitively said 401.
      // Other errors (network, 500, 419) leave state unresolved so
      // a future call with force=true can retry.
      publicAuthResolved = error instanceof HttpError && error.response.status === 401;
      setPublicAuthState(state);

      return state;
    })
    .finally(() => {
      pendingPublicAuthRequest = null;
    });

  return pendingPublicAuthRequest;
}
